<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Детализация пользователя {{this.usage_details.user_name}}</h2>
    </div>

    <!-- Фильтр по дате -->
    <div class="mb-4">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Фильтр по дате</h5>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-4">
              <label for="startDate" class="form-label">Начальная дата</label>
              <input 
                type="date" 
                class="form-control" 
                id="startDate" 
                v-model="filters.startDate"
                @change="applyFilters"
              >
            </div>
            <div class="col-md-4">
              <label for="endDate" class="form-label">Конечная дата</label>
              <input 
                type="date" 
                class="form-control" 
                id="endDate" 
                v-model="filters.endDate"
                @change="applyFilters"
              >
            </div>
            <div class="col-md-4 d-flex align-items-end">
              <button 
                class="btn btn-secondary me-2" 
                @click="clearFilters"
              >
                Очистить
              </button>
              <button 
                class="btn btn-primary" 
                @click="applyFilters"
              >
                Применить
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Информация о фильтрации -->
    <div v-if="filters.startDate || filters.endDate" class="mb-3">
      <small class="text-muted">
        <span v-if="filters.startDate && filters.endDate">
          Показаны данные с {{ formatDate(filters.startDate) }} по {{ formatDate(filters.endDate) }}
        </span>
        <span v-else-if="filters.startDate">
          Показаны данные с {{ formatDate(filters.startDate) }}
        </span>
        <span v-else-if="filters.endDate">
          Показаны данные до {{ formatDate(filters.endDate) }}
        </span>
        <span v-if="this.usage_details.daily_costs.length > 0">
          (найдено записей: {{ this.usage_details.daily_costs.length }})
        </span>
      </small>
    </div>

    <!-- Индикатор загрузки -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
    </div>

    <!-- Данные и график -->
    <div v-else-if="this.usage_details.daily_costs.length > 0">
      <!-- Общая сумма и переключатель вида -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <strong>Общая сумма за период: ${{ totalCost.toFixed(2) }}</strong>
        </div>
        <div class="btn-group" role="group">
          <input type="radio" class="btn-check" name="viewType" id="chartView" v-model="viewType" value="chart" autocomplete="off">
          <label class="btn btn-outline-primary" for="chartView">
            <i class="bi bi-graph-up"></i> График
          </label>
          
          <input type="radio" class="btn-check" name="viewType" id="tableView" v-model="viewType" value="table" autocomplete="off">
          <label class="btn btn-outline-primary" for="tableView">
            <i class="bi bi-table"></i> Таблица
          </label>
        </div>
      </div>

      <!-- График -->
      <div v-if="viewType === 'chart'" class="mb-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">График расходов</h5>
            <div class="btn-group btn-group-sm" role="group">
              <input type="radio" class="btn-check" name="chartType" id="lineChart" v-model="chartType" value="line" autocomplete="off">
              <label class="btn btn-outline-secondary" for="lineChart">
                <i class="bi bi-graph-up"></i> Линейный
              </label>
              
              <input type="radio" class="btn-check" name="chartType" id="barChart" v-model="chartType" value="bar" autocomplete="off">
              <label class="btn btn-outline-secondary" for="barChart">
                <i class="bi bi-bar-chart"></i> Столбчатый
              </label>
            </div>
          </div>
          <div class="card-body">
            <CostChart 
              :key="`chart-${chartType}`"
              :data="usage_details.daily_costs" 
              :chart-type="chartType"
            />
          </div>
        </div>
      </div>

      <!-- Таблица -->
      <div v-if="viewType === 'table'">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>#</th>
              <th>Дата</th>
              <th>Потрачено, USD</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in this.usage_details.daily_costs" :key="item.date">
              <td>{{ index + 1 }}</td>
              <td>{{ formatDate(item.date) }}</td>
              <td>{{ item.total_cost.toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Сообщение об отсутствии данных -->
    <div v-else class="text-center py-5">
      <p class="text-muted">
        <span v-if="filters.startDate || filters.endDate">
          Нет данных за выбранный период
        </span>
        <span v-else>
          Нет данных о расходах
        </span>
      </p>
    </div>

    <!-- Уведомления об ошибках или успешных действиях -->
    <div v-if="toastMessage" class="alert alert-info position-fixed bottom-0 end-0 p-3">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import axiosInstance from '../services/axiosInstance';
import CostChart from '../components/CostChart.vue';

export default {
  components: {
    CostChart
  },
  props: ['id'],
  data() {
    return {
      usage_details: {
        user_name: "",
        daily_costs: []
      },
      filters: {
        startDate: '',
        endDate: ''
      },
      viewType: 'chart', // 'chart' или 'table'
      chartType: 'line', // 'line' или 'bar'
      loading: false,
      toastMessage: '',
    };
  },
  computed: {
    totalCost() {
      return this.usage_details.daily_costs.reduce((sum, item) => sum + item.total_cost, 0);
    }
  },
  methods: {
    async fetchUserDetails() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.startDate) {
          params.start_date = this.filters.startDate;
        }
        if (this.filters.endDate) {
          params.end_date = this.filters.endDate;
        }

        const response = await axiosInstance.get(`/users/${this.id}/usage-details`, { params });
        this.usage_details = response.data;
      } catch (error) {
        this.showToast('Ошибка при загрузке деталей пользователя');
        console.error('Error fetching user details:', error);
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.fetchUserDetails();
    },
    clearFilters() {
      this.filters.startDate = '';
      this.filters.endDate = '';
      this.fetchUserDetails();
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    },
    showToast(message) {
      this.toastMessage = message;
      setTimeout(() => {
        this.toastMessage = '';
      }, 3000);
    },
  },
  mounted() {
    this.fetchUserDetails();
  },
};
</script>

<style>
.table td {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alert {
  z-index: 1050;
}
.btn-check:checked + .btn-outline-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: white;
}
.btn-check:checked + .btn-outline-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}
</style>
